import { LumeFxOrderType, LumeFxSide } from '@/types/lumeFx';
import { invalidStopPriceMessage } from '@/utils/hooks/useOrderBook';
import { InferType, number, object } from 'yup';
import { Nullable } from '../../../model/common';
import { QuantitySchemaParams, quantitySchema, safeString } from './common';

interface StopOrderSchemaParams {
    quantityOptions: QuantitySchemaParams;
    priceOptions: {
        side: LumeFxSide;
        bestAsk: number;
        bestBid: number;
        formattedBestAsk: string;
        formattedBestBid: string;
        ccy2Order: boolean;
    };
}

export const stopOrderSchema = ({ quantityOptions, priceOptions }: StopOrderSchemaParams) => {
    const { side, bestAsk, bestBid, formattedBestAsk, formattedBestBid, ccy2Order } = priceOptions;

    const bestPrice = () => {
        switch (side) {
            case LumeFxSide.BUY:
                return ccy2Order ? formattedBestAsk : formattedBestBid;
            case LumeFxSide.SELL:
                return ccy2Order ? formattedBestBid : formattedBestAsk;
            default:
                return ccy2Order ? formattedBestBid : formattedBestAsk;
        }
    };

    return object({
        type: object({ label: safeString(), value: number() }).required('Stop Type cannot be empty!'),
        stopPrice: number()
            .test('stopPriceConstraint', invalidStopPriceMessage(side, bestPrice(), ccy2Order), (price) => {
                if (side === LumeFxSide.BUY) {
                    if (ccy2Order && +(price || 0) >= bestAsk) {
                        return false;
                    } else if (!ccy2Order && +(price || 0) <= bestBid) {
                        return false;
                    }
                }
                if (side === LumeFxSide.SELL) {
                    if (ccy2Order && +(price || 0) <= bestBid) {
                        return false;
                    } else if (!ccy2Order && +(price || 0) >= bestAsk) {
                        return false;
                    }
                }
                return true;
            })
            .required('Stop Price cannot be empty!')
            .typeError('Please input a number.'),
        stopLimitPrice: number()
            .test('stopLimitPriceRequired', 'Stop Limit Price cannot be empty!', function (value) {
                const { type } = this.parent;
                if (type === LumeFxOrderType.StopLimit) return !!value;
                return true;
            })
            .typeError('Please input a number.'),
        quantity: quantitySchema(quantityOptions),
        duration: object({ label: safeString(), value: number() }).required('Duration cannot be empty!'),
        slippage: safeString()
    });
};

export type StopOrderFormValues = InferType<ReturnType<typeof stopOrderSchema>>;
export type StopOrderFormInput = Nullable<StopOrderFormValues>;
